import {
  Alert,
  Badge,
  Button,
  Checkbox,
  FileInput,
  Group,
  List,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload, IconSend } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { DataTableShell, PageHeader, SortableTh, useInfinitePage, useTableSort } from "../../components/ui";
import { NAV_ICONS } from "../../lib/navCopy";
import { emailCampaignsApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { formatDate } from "../../lib/format";
import type { EmailCampaign } from "../../types/api";
import { axioTokens } from "../../theme";

const CAMPAIGN_STATUS_COLOR: Record<string, string> = {
  completed: "green",
  processing: "yellow",
  failed: "red",
};

export function EmailCampaignsPage() {
  const queryClient = useQueryClient();
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [forceResend, setForceResend] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewCampaign, setViewCampaign] = useState<EmailCampaign | null>(null);

  const templatesQuery = useQuery({
    queryKey: ["email-campaign-templates"],
    queryFn: () => emailCampaignsApi.listTemplates(),
  });

  const campaignsQuery = useInfinitePage(["email-campaigns"], (page, pageSize) =>
    emailCampaignsApi.list({ page, page_size: pageSize }),
  );

  const selectedTemplate = useMemo(
    () => templatesQuery.data?.find((t) => t.key === templateKey) ?? null,
    [templatesQuery.data, templateKey],
  );

  const sendMutation = useMutation({
    mutationFn: () => emailCampaignsApi.send(templateKey!, file!, forceResend),
    onSuccess: (result) => {
      notifications.show({
        color: "green",
        title: "Campaign sent",
        message: `${result.success_count} sent · ${result.skipped_count} skipped · ${result.error_count} failed`,
      });
      setFile(null);
      queryClient.invalidateQueries({ queryKey: ["email-campaigns"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError || err instanceof Error ? err.message : "Could not send campaign",
      });
    },
  });

  async function handleDownloadSample() {
    if (!templateKey) return;
    setDownloading(true);
    try {
      await emailCampaignsApi.downloadSampleCsv(templateKey);
    } catch (err) {
      notifications.show({
        color: "red",
        message: err instanceof Error ? err.message : "Could not download template",
      });
    } finally {
      setDownloading(false);
    }
  }

  const campaigns = campaignsQuery.items;

  const { sorted: sortedCampaigns, sortKey, direction, toggle } = useTableSort(campaigns, {
    template: (c) => c.template_key,
    file: (c) => c.filename,
    status: (c) => c.status,
    sent: (c) => c.success_count,
    skipped: (c) => c.skipped_count,
    failed: (c) => c.error_count,
    date: (c) => new Date(c.created_at),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.emails}
        title="Email campaigns"
        subtitle="Pick a fixed template, download its CSV, fill it in, and upload it to blast the send. Every upload is logged below with per-recipient results."
      />

      <Paper withBorder p="lg">
        <Stack gap="md">
          <Text fw="var(--axio-font-weight-bold)">Send a campaign</Text>

          <Select
            label="Template"
            placeholder="Choose an email to send…"
            data={(templatesQuery.data ?? []).map((t) => ({ value: t.key, label: t.label }))}
            value={templateKey}
            onChange={(v) => {
              setTemplateKey(v);
              setFile(null);
            }}
            disabled={templatesQuery.isLoading}
            maw="var(--axio-unit-480)"
          />

          {selectedTemplate && (
            <Alert color="teal" variant="light" title={selectedTemplate.label}>
              <Text size="sm" mb="var(--axio-unit-6)">
                {selectedTemplate.description}
              </Text>
              <Text size="xs" c="dimmed" mb="var(--axio-unit-4)">
                Required CSV columns:
              </Text>
              <List size="xs" spacing={axioTokens.unit[2]}>
                {selectedTemplate.csv_fields.map((f) => (
                  <List.Item key={f.name}>
                    <Text component="span" fw="var(--axio-font-weight-semibold)">
                      {f.name}
                    </Text>
                    {f.required ? "" : " (optional)"}
                  </List.Item>
                ))}
              </List>
            </Alert>
          )}

          <Group>
            <Button
              variant="light"
              color="teal"
              leftSection={<IconDownload size="var(--axio-unit-16)" />}
              disabled={!templateKey}
              loading={downloading}
              onClick={handleDownloadSample}
            >
              Download CSV template
            </Button>
          </Group>

          <FileInput
            label="Filled CSV"
            placeholder="Upload the CSV with your recipient list"
            accept=".csv,text/csv"
            value={file}
            onChange={setFile}
            disabled={!templateKey}
            clearable
            maw="var(--axio-unit-480)"
          />

          <Checkbox
            label="Resend to addresses already emailed for this campaign"
            checked={forceResend}
            onChange={(e) => setForceResend(e.currentTarget.checked)}
          />

          <Group>
            <Button
              color="teal"
              leftSection={<IconSend size="var(--axio-unit-16)" />}
              disabled={!templateKey || !file}
              loading={sendMutation.isPending}
              onClick={() => sendMutation.mutate()}
            >
              Send campaign
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw="var(--axio-font-weight-bold)">Campaign history</Text>
          <NAV_ICONS.emails size="var(--axio-unit-18)" color="var(--mantine-color-dimmed)" />
        </Group>

        <DataTableShell
          loading={campaignsQuery.isLoading}
          error={
            campaignsQuery.isError
              ? campaignsQuery.error instanceof Error
                ? campaignsQuery.error.message
                : undefined
              : undefined
          }
          onRetry={() => void campaignsQuery.refetch()}
          hasMore={campaignsQuery.hasMore}
          loadingMore={campaignsQuery.loadingMore}
          onLoadMore={campaignsQuery.loadMore}
          isEmpty={campaigns.length === 0}
          empty={{
            icon: NAV_ICONS.emails,
            title: "No campaigns sent yet",
            description: "Upload a CSV above to send your first email campaign.",
          }}
        >
          <Table verticalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableTh columnKey="template" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Template
                </SortableTh>
                <SortableTh columnKey="file" sortKey={sortKey} direction={direction} onSort={toggle}>
                  File
                </SortableTh>
                <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Status
                </SortableTh>
                <SortableTh columnKey="sent" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Sent
                </SortableTh>
                <SortableTh columnKey="skipped" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Skipped
                </SortableTh>
                <SortableTh columnKey="failed" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Failed
                </SortableTh>
                <SortableTh columnKey="date" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Date
                </SortableTh>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedCampaigns.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td fw="var(--axio-font-weight-medium)">{c.template_key}</Table.Td>
                  <Table.Td>{c.filename}</Table.Td>
                  <Table.Td>
                    <Badge color={CAMPAIGN_STATUS_COLOR[c.status] ?? "gray"} variant="light">
                      {c.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td c="green.7" fw="var(--axio-font-weight-semibold)">
                    {c.success_count}
                  </Table.Td>
                  <Table.Td c="dimmed">{c.skipped_count}</Table.Td>
                  <Table.Td c={c.error_count > 0 ? "red.7" : "dimmed"}>{c.error_count}</Table.Td>
                  <Table.Td>{formatDate(c.created_at)}</Table.Td>
                  <Table.Td>
                    {c.errors.length > 0 && (
                      <Button size="xs" variant="subtle" onClick={() => setViewCampaign(c)}>
                        View errors
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </DataTableShell>
      </Stack>

      <Modal
        opened={!!viewCampaign}
        onClose={() => setViewCampaign(null)}
        title={`Errors — ${viewCampaign?.filename ?? ""}`}
        size="lg"
      >
        <Stack gap="xs">
          {(viewCampaign?.errors ?? []).map((e, i) => (
            <Text key={i} size="sm" c="red.7">
              {e.row !== null ? `Row ${e.row}: ` : ""}
              {e.error}
            </Text>
          ))}
          {(viewCampaign?.errors ?? []).length === 0 && <Text c="dimmed">No errors.</Text>}
        </Stack>
      </Modal>
    </Stack>
  );
}
